import { registerLocaleData } from '@angular/common';
import localeDeAt from '@angular/common/locales/de-AT';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTransloco, provideTranslocoMissingHandler } from '@jsverse/transloco';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import Keycloak from 'keycloak-js';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import { provideOptimus } from '@openng/optimus-ui/config';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import {
  AuthenticationModule,
  AuthenticationService,
  AuthorizationService
} from '~ngx-shared/authentication';
import { CalendarModule } from '~ngx-shared/calendar';
import { GlobalErrorHandler } from '~ngx-shared/errorHandlers';
import { apolloOptionsFactory, appInitializerFactory } from '~ngx-shared/factories';
import { FormlyModule } from '~ngx-shared/formly';
import { CustomMissingHandler, NavigationService, TranslocoHttpLoader } from '~ngx-shared/services';
import { environment } from '~shared/environment';
import { environment as karimaEnvironment } from '~shared/environment/karima';
import {
  provideAppRoutingAndHttp,
  provideClientErrorLogging,
  provideOpenobserveRum
} from '~shared/services';
import { routes } from './app.routes';

registerLocaleData(localeDeAt);

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'de-AT' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideClientErrorLogging('karima'),
    provideOpenobserveRum('karima'),
    provideAppInitializer(() => appInitializerFactory(inject(NavigationService))()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideOptimus({
      theme: { options: { darkModeSelector: '.app-dark' } },
      ripple: false
    }),
    provideAppRoutingAndHttp(routes),
    importProvidersFrom(
      AuthenticationModule.forRoot({
        clientId: karimaEnvironment.clientId,
        issuer: environment.issuer,
        realm: environment.realm
      }),
      CalendarModule.forRoot(),
      FormlyModule.forRoot()
    ),
    FormlyModule.forRootProvider(),
    provideApollo(() =>
      apolloOptionsFactory(
        inject(HttpLink),
        inject(AuthenticationService),
        inject(AuthorizationService),
        inject(Keycloak),
        environment.hasuraUrl,
        environment.hasuraWsUrl
      )
    ),
    provideTransloco({
      config: {
        availableLangs: ['de-AT'],
        defaultLang: 'de-AT',
        prodMode: !isDevMode()
      },
      loader: TranslocoHttpLoader
    }),
    provideTranslocoMissingHandler(CustomMissingHandler),
    MessageService,
    ConfirmationService,
    DialogService,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    })
  ]
};
